/**
 * Choosing where the table's audio comes from.
 *
 * Most tables are in one room and want the microphone. A remote table plays
 * over Discord or similar, where the audio Throughline needs is coming *out*
 * of the GM's speakers — so it offers the two routes that actually work:
 *
 *   - a virtual audio cable exposed as an input device (works with any app)
 *   - capturing a browser tab's audio directly (no extra software)
 *
 * Device labels are empty until permission has been granted once, which is
 * why the list is only populated after a probe.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Select } from '@/components/Select';

import type { AudioSource } from '@/audio/MicrophoneSession';

const STORAGE_KEY = 'throughline.audioSource.v1';

export function loadAudioSource(): AudioSource {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as AudioSource;
  } catch {
    /* fall through to the default */
  }
  return { kind: 'microphone' };
}

function saveAudioSource(source: AudioSource): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(source));
  } catch {
    /* private browsing; the choice just won't persist */
  }
}

interface Props {
  value: AudioSource;
  onChange: (source: AudioSource) => void;
  disabled?: boolean;
}

export function AudioSourcePicker({ value, onChange, disabled }: Props) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [needsPermission, setNeedsPermission] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function list(): Promise<void> {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const all = await navigator.mediaDevices.enumerateDevices();
      if (cancelled) return;
      const inputs = all.filter((d) => d.kind === 'audioinput');
      setDevices(inputs);
      // Labels are blank until the user has granted access once, which makes
      // the list useless for picking a virtual cable out of.
      setNeedsPermission(inputs.length > 0 && inputs.every((d) => !d.label));
    }

    void list();
    navigator.mediaDevices?.addEventListener?.('devicechange', list);
    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.('devicechange', list);
    };
  }, []);

  function select(source: AudioSource): void {
    saveAudioSource(source);
    onChange(source);
  }

  async function grantPermission(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === 'audioinput'));
      setNeedsPermission(false);
    } catch {
      /* denial is visible enough from the list staying unlabelled */
    }
  }

  return (
    <div className="source">
      <span className="field__label">Audio source</span>

      {/* One bordered group with hairline-separated segments, rather than
          three loose cards: three cards side by side did not say "pick
          exactly one of these", and a GM reading it could not tell it was a
          choice at all. The icons carry the distinction faster than the
          titles do — a mic, a cable, a browser window. */}
      <div className="source__options" role="radiogroup" aria-label="Audio source">
        <SourceOption
          checked={value.kind === 'microphone'}
          disabled={disabled}
          onSelect={() => select({ kind: 'microphone' })}
          icon={<MicIcon />}
          title="Microphone"
          detail="Everyone is in the room."
        />
        <SourceOption
          checked={value.kind === 'device'}
          disabled={disabled}
          onSelect={() =>
            select({ kind: 'device', deviceId: devices[0]?.deviceId ?? '' })
          }
          icon={<CableIcon />}
          title="Audio input device"
          detail="Route Discord into a virtual cable, then pick it here."
        />
        <SourceOption
          checked={value.kind === 'display'}
          disabled={disabled}
          onSelect={() => select({ kind: 'display' })}
          icon={<TabIcon />}
          title="A browser tab"
          detail="Capture Discord in the browser. No extra software."
        />
      </div>

      {value.kind === 'device' ? (
        <>
          <Select
            label="Input device"
            placeholder="Select an input…"
            disabled={disabled}
            value={value.deviceId ?? ''}
            onChange={(deviceId) => {
              const device = devices.find((d) => d.deviceId === deviceId);
              select({
                kind: 'device',
                deviceId,
                ...(device?.label ? { label: device.label } : {}),
              });
            }}
            options={devices.map((device) => ({
              value: device.deviceId,
              label: device.label || `Input ${device.deviceId.slice(0, 8)}`,
            }))}
          />
          {needsPermission ? (
            <p className="field__hint">
              Device names are hidden until you allow microphone access once.{' '}
              <button
                type="button"
                className="button button--quiet"
                onClick={() => void grantPermission()}
              >
                Show device names
              </button>
            </p>
          ) : (
            <p className="field__hint">
              On macOS, BlackHole or Loopback; on Windows, VB-Cable. Send
              Discord's output there, then select it above.
            </p>
          )}
        </>
      ) : null}

      {value.kind === 'display' ? (
        <p className="field__hint">
          When the share dialog opens, pick the tab running your call and{' '}
          <strong>tick "Share tab audio"</strong> — without it the capture has
          no sound. Chrome and Edge only.
        </p>
      ) : null}
    </div>
  );
}

function SourceOption({
  checked,
  disabled,
  onSelect,
  icon,
  title,
  detail,
}: {
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      disabled={disabled}
      className={checked ? 'source__option is-active' : 'source__option'}
      onClick={onSelect}
    >
      {/* A drawn dot rather than a real radio input: the control is already a
          role="radio" button, and a nested input would be a second focusable
          thing announcing the same state. */}
      <span className="source__dot" aria-hidden="true" />
      <span className="source__icon" aria-hidden="true">
        {icon}
      </span>
      <strong className="source__title">{title}</strong>
      <span className="source__detail">{detail}</span>
    </button>
  );
}

/* Icons are line drawings on a 20-box, stroked with currentColor so they take
   the option's own state — muted at rest, accent when chosen. */
function MicIcon() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none"
      stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round">
      <rect x="7.25" y="2.25" width="5.5" height="9.5" rx="2.75" />
      <path d="M4.5 9.5a5.5 5.5 0 0 0 11 0" />
      <path d="M10 15v2.5M7 17.5h6" />
    </svg>
  );
}

function CableIcon() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none"
      stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 2.5v4M10.5 2.5v4" />
      <path d="M4.5 6.5h8v3a4 4 0 0 1-4 4 4 4 0 0 1-4-4v-3Z" />
      <path d="M8.5 13.5v2a2 2 0 0 0 2 2h5" />
      <circle cx="17" cy="17.5" r="1.6" />
    </svg>
  );
}

function TabIcon() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none"
      stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.25" y="3.75" width="15.5" height="12.5" rx="2" />
      <path d="M2.25 7.75h15.5" />
      <path d="M5.25 5.75h.01M7.75 5.75h.01" />
      <path d="M8 12.25a2.5 2.5 0 0 0 4 0" />
    </svg>
  );
}
